"use client"

import { Link, useLocation } from "react-router-dom"
import {
  LayoutDashboard,
  User,
  Briefcase,
  FileText,
  Settings,
  HelpCircle,
  PackageCheck,
} from "lucide-react"

export default function CaSidebar({ isOpen, toggleSidebar, isMobile }) {
  const location = useLocation()

  const navItems = [
    { name: "CA Compliances", icon: FileText, href: "/ticket-interface", description: "Chartered Accountant Tasks" },
  ]

  const getSidebarWidth = () => {
    if (isMobile) return 0
    return 80
  }

  return (
    <>
      {/* Mobile overlay */}
      {isMobile && isOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-all duration-300"
          onClick={toggleSidebar}
        />
      )}

      <div
        className={`fixed inset-y-0 left-0 z-50 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 text-white transition-all duration-300 border-r border-slate-700/50 shadow-2xl flex flex-col ${
          isMobile
            ? isOpen
              ? "w-64 xs:w-72 sm:w-80 translate-x-0"
              : "w-64 xs:w-72 sm:w-80 -translate-x-full"
            : "w-16 sm:w-18 md:w-20 lg:w-20 xl:w-20"
        }`}
      >
        {/* Close button for mobile */}
        {isMobile && (
          <button
            onClick={toggleSidebar}
            className="absolute top-3 right-3 xs:top-4 xs:right-4 text-white/70 hover:text-white z-10 p-1.5 xs:p-2 rounded-lg hover:bg-white/10 transition-all duration-200"
          >
            <svg className="w-4 h-4 xs:w-5 xs:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        {/* Logo Section */}
        <div
          className={`flex items-center flex-shrink-0 p-3 xs:p-4 sm:p-6 h-16 xs:h-18 sm:h-20 border-b border-slate-700/50 ${
            isMobile ? "justify-start" : "justify-center"
          }`}
        >
          <div className="w-8 h-8 xs:w-9 xs:h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg xs:rounded-xl flex items-center justify-center shadow-lg relative group">
            <span className="text-sm xs:text-base sm:text-lg font-bold text-white">T</span>
            {!isMobile && (
              <div
                className="fixed px-3 py-2 bg-slate-800 text-white text-sm rounded-lg shadow-2xl border border-slate-600/50 opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none whitespace-nowrap z-[9999]"
                style={{
                  left: `${getSidebarWidth() + 8}px`,
                  top: "2rem",
                }}
              >
                <div className="font-semibold text-white">Tactos CA</div>
                <div className="text-xs text-slate-300 mt-1">Compliance Panel</div>
                <div
                  className="absolute top-1/2 transform -translate-y-1/2 w-2 h-2 bg-slate-800 rotate-45 border-r border-t border-slate-600/50"
                  style={{ left: "-4px" }}
                />
              </div>
            )}
          </div>
          {isMobile && (
            <div className="ml-3 xs:ml-4">
              <span className="text-lg xs:text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Tactos
              </span>
              <p className="text-xs text-slate-400 mt-0.5">CA Panel</p>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex-1 overflow-hidden">
          <nav className="h-full overflow-y-auto scrollbar-thin scrollbar-thumb-slate-600 scrollbar-track-slate-800 px-2 xs:px-3 py-4 xs:py-6 sm:py-8">
            <ul className="space-y-2 xs:space-y-3">
              {navItems.map((item, index) => {
                const IconComponent = item.icon
                const isActive = location.pathname === item.href
                return (
                  <li key={item.name} className="relative group">
                    <Link
                      to={item.href}
                      className={`flex items-center rounded-lg xs:rounded-xl transition-all duration-300 relative overflow-visible ${
                        isActive
                          ? "bg-gradient-to-r from-blue-500/20 to-purple-500/20 text-blue-400 shadow-lg shadow-blue-500/10 scale-105"
                          : "hover:bg-white/5 text-slate-300 hover:text-white hover:scale-105"
                      } ${isMobile ? "justify-start p-3 xs:p-3.5 sm:p-4" : "justify-center p-2.5 xs:p-3 sm:p-4"}`}
                      onClick={isMobile ? toggleSidebar : undefined}
                    >
                      {isActive && (
                        <div className="absolute left-0 top-0 bottom-0 w-0.5 xs:w-1 bg-gradient-to-b from-blue-400 to-purple-400 rounded-r-full" />
                      )}
                      <div className={`flex-shrink-0 w-5 h-5 xs:w-6 xs:h-6 flex items-center justify-center ${isActive ? "text-blue-400" : ""}`}>                        
                        <IconComponent className="w-4 h-4 xs:w-5 xs:h-5 sm:w-6 sm:h-6" />
                      </div>
                      {isMobile && <span className="ml-3 xs:ml-4 font-medium text-sm xs:text-base">{item.name}</span>}
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/0 to-purple-500/0 hover:from-blue-500/5 hover:to-purple-500/5 rounded-lg xs:rounded-xl transition-all duration-300" />
                    </Link>
                    {!isMobile && (
                      <div
                        className="fixed px-3 py-2 bg-slate-800 text-white text-sm rounded-lg shadow-2xl border border-slate-600/50 opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none whitespace-nowrap z-[9999]"
                        style={{
                          left: `${getSidebarWidth() + 8}px`,
                          top: `${120 + index * 56 + 32}px`,
                          transform: "translateY(-50%)",
                        }}
                      >
                        <div className="font-semibold text-white">{item.name}</div>
                        <div className="text-xs text-slate-300 mt-1">{item.description}</div>
                        <div
                          className="absolute top-1/2 transform -translate-y-1/2 w-2 h-2 bg-slate-800 rotate-45 border-r border-t border-slate-600/50"
                          style={{ left: "-4px" }}
                        />
                      </div>
                    )}
                  </li>
                )
              })}
            </ul>
          </nav>
        </div>
      </div>

      {/* Custom scrollbar styles */}
      <style jsx global>{`
        .scrollbar-thin {
          scrollbar-width: thin;
        }
        .scrollbar-thumb-slate-600::-webkit-scrollbar-thumb {
          background-color: rgb(71 85 105);
          border-radius: 0.375rem;
        }
        .scrollbar-track-slate-800::-webkit-scrollbar-track {
          background-color: rgb(30 41 59);
        }
        .scrollbar-thin::-webkit-scrollbar {
          width: 4px;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb {
          background-color: rgb(71 85 105);
          border-radius: 0.375rem;
        }
        .scrollbar-thin::-webkit-scrollbar-track {
          background-color: rgb(30 41 59);
        }
        @media (max-width: 768px) {
          .scrollbar-thin::-webkit-scrollbar {
            width: 0px;
            background: transparent;
          }
          .scrollbar-thin {
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
        }
        .scrollbar-thin {
          scroll-behavior: smooth;
          -webkit-overflow-scrolling: touch;
        }
        body {
          overflow-x: hidden;
        }
      `}</style>
    </>
  )
}
